import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import i18n, { I18N_NS } from "Config/i18n";
import { DragEventHandler, useCallback, useState } from "react";
import { fileUploadStore, InitModalOpts, UploadMode } from "../FileUpload/FileUploadStore";
import { documentsTableStore } from "../Stores/DocumentsTableStore";
import { waitFor } from "utils/waitFor";
import { message } from "antd";
import { useTranslation } from "react-i18next";

interface DocumentsDropProps {
  children: React.ReactNode;
  disabled?: boolean;
  uploadMode: UploadMode;
}

export const DocumentsDrop = ({ children, uploadMode, disabled }: DocumentsDropProps) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const { t } = useTranslation(I18N_NS.PROJECT);
  const dropHandler: DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    if (disabled) {
      return;
    }
    const files: File[] = [];
    let isDirectory = false;
    const entries = e.dataTransfer.items || e.dataTransfer.files;
    for (const item of Array.from<DataTransferItem | File>(entries)) {
      // If dropped items aren't files, reject them
      if (item instanceof File) {
        files.push(item);
        continue;
      }
      if (item.kind === "file") {
        const entry = item.webkitGetAsEntry();
        if (entry.isDirectory) {
          isDirectory = true;
          break;
        }
        const file = item.getAsFile();
        files.push(file);
      }
    }
    if (isDirectory) {
      message.error(t("Directory upload is not supported"));
    }
    if (!isDirectory && files.length !== 0) {
      processDrop(files);
    }
    setIsDragActive(false);
  };

  const dragOverHandler: DragEventHandler<HTMLDivElement> = (ev) => {
    if (disabled) {
      return;
    }
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();
    const hasFiles = Array.from(ev.dataTransfer.items).some((item) => item.kind === "file");
    if (hasFiles) {
      setIsDragActive(true);
    }
  };

  const dragLeaveHandler: DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    const hasFiles = Array.from(e.dataTransfer.items).some((item) => item.kind === "file");
    if (hasFiles) {
      setIsDragActive(false);
    }
  };

  const processDrop = useCallback(
    async (files: File[]) => {
      try {
        const initData: InitModalOpts = {};
        if (uploadMode === "VIEW") {
          initData.module = "VIEW";
        } else {
          initData.folderId = documentsTableStore.currentFolderId;
        }

        await fileUploadStore.initModal(initData);
        await waitFor(500);
        fileUploadStore.setFilesToUpload(files);
      } catch (error) {
        throw error;
      }
    },
    [uploadMode]
  );
  return (
    <div
      className={isDragActive ? "documents-dnd dragover" : "documents-dnd"}
      data-test-id="dropzone"
      onDrop={dropHandler}
      onDragOver={dragOverHandler}
    >
      <div className="documents-dnd-placeholder">
        <div>
          <FontAwesomeIcon icon={["fas", "file-upload"]} fixedWidth={true} size="lg" className="mb-2" />
          <div>{i18n.t("Drop here to start upload", { ns: I18N_NS.PROJECT }) as string}</div>
        </div>
      </div>
      {isDragActive ? (
        <div
          onDragLeave={dragLeaveHandler}
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            zIndex: 100,
            background: "url(data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7)",
          }}
        ></div>
      ) : null}
      {children}
    </div>
  );
};
